/**
 * EPIC283-E Over time presenter.
 * Consumes K1 over_time windows honestly — no frontend-derived trends,
 * no update_mode stamping, no invented issue/location or causal claims.
 */

import {
  ROLLING_24H_ANCHORED_REASON,
  ROLLING_24H_MIN_PERIODS_FOR_DAY_TOTAL,
  recurringSupportingActivityTotal,
  supportingActivityTotal,
  type SiteConditionMeasurementWindow,
} from '../continuousSafety/siteConditionState';
import { formatSiteMonthDay } from '../siteTime/siteTime';
import {
  comparabilityLabel,
  displayStateContent,
  formatDisplayMessage,
  type DisplayStateKey,
} from './contentContract';

/** K1 window ids for Recurring. 1D uses backend `rolling_24h` (PT15M source). */
export type OverTimeWindowId =
  | 'rolling_24h'
  | 'rolling_7d'
  | 'rolling_30d'
  | 'rolling_90d';

/** Default while history is short / still building (EPIC284 Recurring 1D). */
export const DEFAULT_OVER_TIME_WINDOW: OverTimeWindowId = 'rolling_24h';

export const OVER_TIME_WINDOW_OPTIONS: {
  id: OverTimeWindowId;
  days: 1 | 7 | 30 | 90;
  label: string;
  shortLabel: string;
}[] = [
  { id: 'rolling_24h', days: 1, label: 'Last 24 hours', shortLabel: '1 day' },
  { id: 'rolling_7d', days: 7, label: 'Last 7 days', shortLabel: '7 days' },
  { id: 'rolling_30d', days: 30, label: 'Last 30 days', shortLabel: '30 days' },
  { id: 'rolling_90d', days: 90, label: 'Last 90 days', shortLabel: '90 days' },
];

const KNOWN_STATES: ReadonlySet<string> = new Set([
  'current',
  'current_with_limitations',
  'delayed',
  'stale',
  'never_measured',
  'scan_only_collecting',
  'unavailable',
  'readiness_unavailable',
  'empty',
  'blocked',
  'unauthorized',
  'failure',
]);

export type OverTimeWindowChoice = {
  id: OverTimeWindowId;
  days: 1 | 7 | 30 | 90;
  label: string;
  shortLabel: string;
  availability: string;
  displayState: string;
  eligible: boolean;
  isPartial: boolean;
};

export type OverTimePresentation = {
  stateKey: DisplayStateKey;
  stateLabel: string;
  message: string;
  limitation: string;
  primaryCta: string;
  windowLabel: string;
  asOfLabel: string;
  selectedWindowId: OverTimeWindowId;
  windowChoices: OverTimeWindowChoice[];
  /** One material story when K1 supports it; honest empty/unavailable otherwise. */
  storyLine: string;
  basisSummary: string | null;
  coverageSummary: string | null;
  finalitySummary: string | null;
  comparabilitySummary: string;
  /** True when rate/trend claims must not be shown. */
  comparisonBlocked: boolean;
  methodologySummary: string;
};

function mapDisplayStateKey(raw: string | null | undefined): DisplayStateKey {
  if (!raw) return 'unavailable';
  const normalized = raw.trim().toLowerCase().replace(/-/g, '_');
  if (normalized === 'current_with_limitation') return 'current_with_limitations';
  if (KNOWN_STATES.has(normalized)) return normalized as DisplayStateKey;
  return 'unavailable';
}

export function parseOverTimeWindowId(
  raw: string | null | undefined,
): OverTimeWindowId {
  if (
    raw === '1'
    || raw === '1d'
    || raw === '24h'
    || raw === 'rolling_1d'
    || raw === 'rolling_24h'
  ) {
    return 'rolling_24h';
  }
  if (raw === '7' || raw === '7d' || raw === 'rolling_7d') return 'rolling_7d';
  if (raw === '90' || raw === '90d' || raw === 'rolling_90d') return 'rolling_90d';
  if (raw === '30' || raw === '30d' || raw === 'rolling_30d') return 'rolling_30d';
  return DEFAULT_OVER_TIME_WINDOW;
}

/** Compact query value for `ot_window` (1 / 7 / 30 / 90). */
export function encodeOverTimeWindowParam(id: OverTimeWindowId): string {
  if (id === 'rolling_24h') return '1';
  if (id === 'rolling_7d') return '7';
  if (id === 'rolling_90d') return '90';
  return '30';
}

/** Declared resolution for Recurring window kinds (not Current COW). */
export function recurringWindowResolution(id: OverTimeWindowId): string {
  if (id === 'rolling_24h') return 'PT15M';
  if (id === 'rolling_7d' || id === 'rolling_30d' || id === 'rolling_90d') {
    return 'PT1H';
  }
  return '';
}

/** Short calendar day for anchored Recurring face copy (e.g. "Aug 2"). */
export function formatMeasuredCalendarDay(
  raw: string | null | undefined,
  timeZone?: string | null,
): string | null {
  if (!raw || raw === '—') return null;
  const isUtcDayBoundary = /T00:00:00(\.\d+)?Z$/i.test(String(raw).trim());
  const formatted = formatSiteMonthDay(raw, isUtcDayBoundary ? 'UTC' : timeZone);
  return formatted || null;
}

/**
 * Customer Window label for Recurring. Anchored/stale 1D must not pretend to be
 * wall-clock "Last 24 hours" when the query rebounded to last observed day.
 * When a measured end exists, name the day so the face cannot be read as today.
 */
export function formatRecurringCustomerWindowLabel(
  windowId: OverTimeWindowId,
  window: SiteConditionMeasurementWindow | null | undefined,
  timeZone?: string | null,
): string {
  const meta = windowMeta(windowId);
  if (windowId !== 'rolling_24h') return meta.label;
  const reasons = window?.reasonCodes || [];
  if (reasons.includes(ROLLING_24H_ANCHORED_REASON)) {
    const measuredDay = formatMeasuredCalendarDay(
      resolveIncludedMeasurementThrough(window ?? null)
        || resolveOverTimeHistoryAsOf(window ?? null),
      timeZone ?? window?.summary.measurementCoverage?.frozenBasis?.siteTimezone,
    );
    return measuredDay
      ? `Last measured day ends ${measuredDay}`
      : 'Last measured day';
  }
  const periods = window?.summary.bucketCount || window?.buckets.length || 0;
  if (periods > 0 && periods < ROLLING_24H_MIN_PERIODS_FOR_DAY_TOTAL) {
    return 'Last partial 24 hours';
  }
  return meta.label;
}

function windowMeta(id: OverTimeWindowId) {
  return OVER_TIME_WINDOW_OPTIONS.find((option) => option.id === id)!;
}

function isPartialWindow(window: SiteConditionMeasurementWindow | null): boolean {
  if (!window) return false;
  if (window.availability === 'limited') return true;
  if (window.buckets.some((bucket) => bucket.isPartial)) return true;
  const state = mapDisplayStateKey(window.displayState);
  return state === 'current_with_limitations';
}

function resolveFinality(window: SiteConditionMeasurementWindow | null): string | null {
  if (!window || window.buckets.length === 0) return null;
  const allFinalized = window.buckets.every(
    (bucket) => bucket.bucketFinality === 'finalized',
  );
  return allFinalized ? 'finalized' : 'provisional';
}

function laterTimestamp(current: string | null, candidate: string | null): string | null {
  if (!candidate || candidate === '—') return current;
  if (!current || current === '—') return candidate;
  const currentMs = Date.parse(current);
  const candidateMs = Date.parse(candidate);
  if (Number.isFinite(currentMs) && Number.isFinite(candidateMs)) {
    return candidateMs >= currentMs ? candidate : current;
  }
  return candidate > current ? candidate : current;
}

/**
 * Max end of actual included OM rows (bucket_end / interval.end).
 * Never the mathematical rolling-window envelope (`history_as_of` / provenance windowEnd).
 */
export function resolveIncludedMeasurementThrough(
  window: SiteConditionMeasurementWindow | null,
): string | null {
  if (!window) return null;
  let latest: string | null = null;
  for (const bucket of window.buckets || []) {
    latest = laterTimestamp(latest, bucket.interval?.end || null);
    latest = laterTimestamp(latest, bucket.observedThrough);
    latest = laterTimestamp(latest, bucket.displayAsOf);
  }
  return latest;
}

function latestBucketAsOf(window: SiteConditionMeasurementWindow): string | null {
  return resolveIncludedMeasurementThrough(window);
}

/**
 * Recurring window envelope / history cursor.
 * Prefer provenance window_end / history_as_of so operators can still inspect
 * the 24h lookback bound. Customer "measured through" must use
 * {@link resolveIncludedMeasurementThrough} instead.
 */
export function resolveOverTimeHistoryAsOf(
  window: SiteConditionMeasurementWindow | null,
): string {
  if (!window) return '—';
  const candidates = [
    window.operationalMeasurementProvenance?.windowEnd,
    window.historyAsOf,
    latestBucketAsOf(window),
    window.displayAsOf,
    window.observedThrough,
    window.buckets[0]?.displayAsOf,
    window.buckets[0]?.observedThrough,
  ].filter((value): value is string => Boolean(value && value !== '—'));
  if (candidates.length === 0) return '—';
  let latest = candidates[0];
  for (const candidate of candidates.slice(1)) {
    latest = laterTimestamp(latest, candidate) || latest;
  }
  return latest;
}

function resolveAsOf(window: SiteConditionMeasurementWindow | null): string {
  return resolveIncludedMeasurementThrough(window) || resolveOverTimeHistoryAsOf(window);
}

function resolveStateKey(
  window: SiteConditionMeasurementWindow | null,
): DisplayStateKey {
  if (!window) return 'unavailable';
  if (
    window.availability === 'unavailable'
    || window.displayState === 'unavailable'
    || window.buckets.length === 0
  ) {
    // Empty window with no buckets is cold-start / unavailable — not a fabricated story.
    return window.buckets.length === 0 && window.availability !== 'unavailable'
      ? 'empty'
      : 'unavailable';
  }
  if (
    window.summary.bucketCount > 0
    && window.summary.measurementValue == null
    && supportingActivityTotal(window) == null
    && Object.keys(window.summary.activityCounts || {}).length === 0
  ) {
    // Prefer explicit display_state when present; otherwise empty.
    const mapped = mapDisplayStateKey(window.displayState);
    if (mapped === 'unavailable' || mapped === 'never_measured') return mapped;
  }
  return mapDisplayStateKey(window.displayState);
}

function comparisonBlocked(
  window: SiteConditionMeasurementWindow | null,
): boolean {
  if (!window) return true;
  const status = (window.comparisonStatus || '').toLowerCase();
  if (!status || status === 'comparison_unavailable' || status === 'not_comparable') {
    return true;
  }
  if (status === 'comparable') return false;
  // Unknown / limited statuses: do not invent trend eligibility.
  return status !== 'limited';
}

function comparabilitySummary(
  window: SiteConditionMeasurementWindow | null,
  blocked: boolean,
): string {
  if (!window) {
    return `${comparabilityLabel('cannot_compare')} — over-time window is unavailable.`;
  }
  const status = (window.comparisonStatus || '').toLowerCase();
  if (status === 'comparable') {
    return `${comparabilityLabel('valid')}. Rates and direction require this comparable basis.`;
  }
  if (status === 'limited') {
    return `${comparabilityLabel('limited')} — do not treat change as final.`;
  }
  const reason = window.reasonCodes[0]
    ? ` Reason: ${window.reasonCodes[0].replace(/_/g, ' ')}.`
    : '';
  if (blocked) {
    return `${comparabilityLabel('cannot_compare')}.${reason} No rate or trend claim.`;
  }
  return `${comparabilityLabel('cannot_compare')}.${reason}`;
}

function basisSummary(window: SiteConditionMeasurementWindow | null): string | null {
  if (!window?.basis?.type) {
    return 'Measurement basis is not available for this window.';
  }
  const units = window.basis.units ? ` · ${window.basis.units}` : '';
  const version = window.basis.version ? ` · ${window.basis.version}` : '';
  return `Basis: ${window.basis.type}${units}${version}`;
}

function coverageSummary(
  window: SiteConditionMeasurementWindow | null,
  partial: boolean,
): string | null {
  if (!window) return 'Coverage unavailable for this window.';
  const bucketCount = window.summary.bucketCount || window.buckets.length;
  if (partial) {
    return `Partial window — ${bucketCount} measured bucket${
      bucketCount === 1 ? '' : 's'
    }; missing days are not invented.`;
  }
  if (bucketCount === 0) {
    return 'No measured buckets in this window yet.';
  }
  return `${bucketCount} measured bucket${bucketCount === 1 ? '' : 's'} in this window.`;
}

function finalitySummary(finality: string | null): string | null {
  if (!finality) return null;
  if (finality === 'finalized') {
    return 'Finality: finalized for the buckets in this window.';
  }
  return 'Finality: provisional — not a final longitudinal claim.';
}

function buildStoryLine(params: {
  window: SiteConditionMeasurementWindow | null;
  windowId: OverTimeWindowId;
  meta: ReturnType<typeof windowMeta>;
  stateKey: DisplayStateKey;
  blocked: boolean;
}): string {
  const { window, windowId, meta, stateKey, blocked } = params;
  const customerWindowLabel = formatRecurringCustomerWindowLabel(windowId, window);
  if (!window || stateKey === 'unavailable') {
    return `No ${meta.shortLabel.toLowerCase()} measurement story is available yet. History is not fabricated.`;
  }
  if (stateKey === 'empty' || stateKey === 'never_measured') {
    return `No qualifying measured activity in the ${customerWindowLabel.toLowerCase()} window.`;
  }
  if (stateKey === 'stale') {
    return `Longitudinal measurement is stale for the ${customerWindowLabel.toLowerCase()} window; do not treat prior values as current trend.`;
  }

  // Facts surface: bucket activity only. Never fall back to claim measurementValue.
  // Recurring 1D uses fail-closed day-total semantics (no sparse/anchored Σ).
  const value = recurringSupportingActivityTotal(window, windowId);
  const hasValue = value != null && Number.isFinite(value);
  if (!hasValue) {
    if ((window.summary.bucketCount || window.buckets.length) > 0) {
      return `Measured periods exist for ${customerWindowLabel.toLowerCase()}, but a day total is withheld until the window is complete and wall-clock aligned.`;
    }
    return `Measured activity is present for ${customerWindowLabel.toLowerCase()}, but no aggregate value is available yet.`;
  }

  if (blocked) {
    return `Qualifying activity over ${customerWindowLabel.toLowerCase()}: ${value}. Comparison is not valid — no rate or direction claim.`;
  }

  // Honest material story: supporting total + window only. Never invent rate/trend.
  return `Qualifying activity over ${customerWindowLabel.toLowerCase()}: ${value}. Recurring concentration labels are not available yet.`;
}

function buildWindowChoices(
  windows: Record<string, SiteConditionMeasurementWindow> | null | undefined,
): OverTimeWindowChoice[] {
  return OVER_TIME_WINDOW_OPTIONS.map((option) => {
    const window = windows?.[option.id] || null;
    const availability = window?.availability || 'unavailable';
    const displayState = window?.displayState || 'unavailable';
    const partial = isPartialWindow(window);
    const eligible =
      Boolean(window)
      && availability !== 'unavailable'
      && displayState !== 'unavailable'
      && (window?.buckets.length || 0) > 0;
    return {
      id: option.id,
      days: option.days,
      label: option.label,
      shortLabel: option.shortLabel,
      availability,
      displayState,
      eligible,
      isPartial: partial,
    };
  });
}

/**
 * Present Over time for a selected K1 window.
 * Does not invent trends, issue/location, or update_mode.
 */
export function presentOverTime(params: {
  windows?: Record<string, SiteConditionMeasurementWindow> | null;
  selectedWindowId?: OverTimeWindowId | null;
  dataNote?: string | null;
}): OverTimePresentation {
  const selectedWindowId = params.selectedWindowId || DEFAULT_OVER_TIME_WINDOW;
  const meta = windowMeta(selectedWindowId);
  const window = params.windows?.[selectedWindowId] || null;
  const customerWindowLabel = formatRecurringCustomerWindowLabel(
    selectedWindowId,
    window,
  );
  const partial = isPartialWindow(window);
  const blocked = comparisonBlocked(window);
  const stateKey = resolveStateKey(window);
  const block = displayStateContent(stateKey);
  const asOfLabel = resolveAsOf(window);
  const finality = resolveFinality(window);

  let message =
    stateKey === 'unavailable' || stateKey === 'empty' || stateKey === 'never_measured'
      ? block.message
      : formatDisplayMessage(stateKey, asOfLabel);

  // Over time framing — never look like Trusted Now / Current shift.
  if (stateKey === 'current' || stateKey === 'current_with_limitations') {
    message = `${customerWindowLabel} through ${asOfLabel}`;
  } else if (stateKey === 'stale' || stateKey === 'delayed') {
    message = formatDisplayMessage(stateKey, asOfLabel);
  }

  let limitation =
    partial && stateKey === 'current'
      ? 'This measurement window is marked limited or partial.'
      : block.limitation;
  if (partial && stateKey !== 'unavailable') {
    limitation = [
      'Partial longitudinal window — do not invent missing days.',
      limitation,
    ]
      .filter(Boolean)
      .join(' ');
  }
  if (blocked && stateKey !== 'unavailable' && stateKey !== 'empty') {
    limitation = [
      limitation,
      'Valid comparison is unavailable; no rate or trend is claimed.',
    ]
      .filter(Boolean)
      .join(' ');
  }
  if (params.dataNote) {
    limitation = `${limitation} ${params.dataNote}`.trim();
  }

  return {
    stateKey,
    stateLabel: block.label,
    message,
    limitation,
    primaryCta: block.primaryCta,
    windowLabel: customerWindowLabel,
    asOfLabel,
    selectedWindowId,
    windowChoices: buildWindowChoices(params.windows),
    storyLine: buildStoryLine({
      window,
      windowId: selectedWindowId,
      meta,
      stateKey,
      blocked,
    }),
    basisSummary: basisSummary(window),
    coverageSummary: coverageSummary(window, partial),
    finalitySummary: finalitySummary(finality),
    comparabilitySummary: comparabilitySummary(window, blocked),
    comparisonBlocked: blocked,
    methodologySummary:
      'Over-time measurement describes observed activity under a declared basis — not whether site conditions are good or bad, and not causal control effectiveness.',
  };
}

/** Extract K1 over_time map from the first condition (passthrough; may be empty). */
export function overTimeWindowsFromSiteConditionResponse(data: {
  conditions?: Array<{
    operationalMeasurements?: {
      overTime?: Record<string, SiteConditionMeasurementWindow>;
    };
  }>;
} | null | undefined): Record<string, SiteConditionMeasurementWindow> {
  return data?.conditions?.[0]?.operationalMeasurements?.overTime || {};
}
